import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, IconButton, Tooltip, Alert, Typography } from '@mui/material';
import { Bookmark as BookmarkIcon, BookmarkBorder as BookmarkBorderIcon, Warning as WarningIcon } from '@mui/icons-material';
import { MagicReportSection } from './MagicReportSection';
import { CommentsSection } from './CommentsSection';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { CHANGE_ORDERS_SECTION_CONFIG } from './sections.config';
import { DrawContext, SectionFieldCollection, SectionFieldDTO, FieldType } from '../magic-report.type';
import { getCommentsField } from './section-utils';
import { filterVisibleCoLienDraws, isCoLienHidden, resolveHiddenCoLienDrawIds } from './co-lien-visibility.util';
import { ChangeOrdersTable, ChangeOrderRow, changeOrderRowKey, rowMatchesLineItem } from './ChangeOrdersTable';
import { useMagicReport } from '../context/MagicReportContext';
import { formatCurrency } from '../../../../common/utils/common.util';
import { LineItemLabelEnum } from '../../../glossary/glossary.type';
import { ProjectLineItemDTO } from '../../../project/project.type';

const ROWS_FIELD = 'ChangeOrdersRows';
const COMMENT_1_FIELD = 'ChangeOrdersComment1';
const COMMENT_2_FIELD = 'ChangeOrdersComment2';
const SUMMARY_ORIGINAL_CONTRACT_SUM = 'ChangeOrdersSummaryOriginalContractSum';
const SUMMARY_TOTAL_NET_CHANGE_ORDER = 'ChangeOrdersSummaryTotalNetChangeOrder';
const SUMMARY_TOTAL_NET_REALLOCATION = 'ChangeOrdersSummaryTotalNetReallocation';
const SUMMARY_CONTRACT_SUM_TO_DATE = 'ChangeOrdersSummaryContractSumToDate';

/**
 * Per-draw summary field name. Every covered draw gets its own set; the un-suffixed originals are
 * also kept in sync with the primary draw so the PDF export and Action Items keep working for
 * single-draw reports without change.
 */
export const changeOrdersSummaryFieldName = (base: string, drawId: string) => `${base}__${drawId}`;

const COMMENT_2_DEFAULT_TEXT =
  "In our opinion, and with the limited documentation provided, without performing a cost analysis of the changes, but relying solely upon our judgment, the cost of the new changes are reasonably priced. Although we recommend the Lender's Appraiser make any judgments as to the effect of the changes on the value of the project, in our opinion, the proposed changes to the Work do not equate to material reductions and will not negatively affect the value of the Lender's collateral.";

/** Older saved rows stored `coNumber` as a number; every consumer here expects a string. */
const normalizeRow = (row: ChangeOrderRow): ChangeOrderRow =>
  row?.coNumber != null && typeof row.coNumber !== 'string' ? { ...row, coNumber: String(row.coNumber) } : row;

const joinWithAnd = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

// Format CO numbers. If all values are integers, collapses consecutive runs into ranges.
// Mixed/non-numeric values are joined with commas and "and".
const formatCONumbers = (numbers: string[]): string => {
  if (numbers.length === 0) return '';
  if (numbers.length === 1) return numbers[0];

  const parsed = numbers.map(n => ({ raw: String(n ?? ''), num: parseInt(String(n ?? ''), 10) }));
  const allIntegers = parsed.every(p => !isNaN(p.num) && String(p.num) === p.raw.trim());

  if (!allIntegers) {
    return joinWithAnd(numbers);
  }

  const sorted = [...parsed].sort((a, b) => a.num - b.num);
  const ranges: string[] = [];
  let start = sorted[0].num;
  let end = sorted[0].num;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].num === end + 1) {
      end = sorted[i].num;
    } else {
      ranges.push(start === end ? `${start}` : end - start === 1 ? `${start} and ${end}` : `${start}-${end}`);
      start = sorted[i].num;
      end = sorted[i].num;
    }
  }
  ranges.push(start === end ? `${start}` : end - start === 1 ? `${start} and ${end}` : `${start}-${end}`);

  return joinWithAnd(ranges);
};

// Format CO numbers for the initial mention (e.g., "1-4" for a consecutive run).
const formatInitialCONumbers = (numbers: string[]): string => {
  if (numbers.length === 0) return '';
  if (numbers.length === 1) return numbers[0];

  const parsed = numbers.map(n => ({ raw: String(n ?? ''), num: parseInt(String(n ?? ''), 10) }));
  const allIntegers = parsed.every(p => !isNaN(p.num) && String(p.num) === p.raw.trim());

  if (!allIntegers) {
    return joinWithAnd(numbers);
  }

  const sorted = [...parsed].sort((a, b) => a.num - b.num);
  const isConsecutive = sorted.every((p, idx) => idx === 0 || p.num === sorted[idx - 1].num + 1);

  if (isConsecutive) {
    return `${sorted[0].num}-${sorted[sorted.length - 1].num}`;
  }
  return formatCONumbers(numbers);
};

const getRowCOValue = (row: ChangeOrderRow): number => {
  if (row.prevCO !== undefined || row.currCO !== undefined) {
    return (row.prevCO || 0) + (row.currCO || 0);
  }
  return (row as any).newCO || 0;
};

const generateComment1 = (rows: ChangeOrderRow[], lineItems: any[], projectDrawName = ''): string => {
  // Only current-month COs are "new" — exclude rows carried over from last month
  const newRows = rows.filter(r => !r.isLastMonth);
  if (!newRows.length) {
    return 'No new change orders were included with the current application for payment.';
  }
  rows = newRows;

  const totalCO = rows.reduce((sum, row) => sum + getRowCOValue(row), 0);
  const formattedTotal = formatCurrency(totalCO.toString(), true);

  // Get CO numbers and descriptions
  const coNumbers = rows.map(r => r.coNumber);
  const coNumbersText = formatInitialCONumbers(coNumbers);

  // Determine description text
  const descriptions = rows.map(r => r.description).filter(Boolean);
  const descriptionText =
    descriptions.length === 0
      ? 'Multiple changes'
      : descriptions.length === 1
        ? descriptions[0]
        : descriptions.length === 2
          ? `${descriptions[0]} and ${descriptions[1]}`
          : 'Multiple changes';

  // Category 1: Missing Documents - CO is missing if "Do we have the document?" = No
  const missingDocs = rows.filter(r => r.doWeHaveDoc === 'No');
  const missingDocNumbers = missingDocs.map(r => r.coNumber);

  // Category 2: Not Fully Executed - Document exists AND not signed by BOTH Owner and Contractor
  // Architect signature does NOT affect execution status - only Contractor and Owner matter
  // Logic: Document = Yes AND (Contractor = No OR Owner = No)
  const insufficientlySigned = rows.filter(r => r.doWeHaveDoc === 'Yes' && (r.contractor === 'No' || r.owner === 'No'));
  const insufficientlySignedNumbers = insufficientlySigned.map(r => r.coNumber);

  // Check if ALL documents are present (all rows have doWeHaveDoc === 'Yes')
  const allDocsPresent = rows.length > 0 && rows.every(r => r.doWeHaveDoc === 'Yes');

  // Build comment based on conditions
  if (missingDocs.length === 0 && insufficientlySigned.length === 0) {
    // Typical case - all docs present and signed
    return `New Change Orders ${coNumbersText} (${descriptionText}: ${formattedTotal}) were included with the current application for payment. The change orders have been signed by the Owner and Contractor.`;
  } else if (missingDocs.length > 0 && insufficientlySigned.length === 0) {
    // Only missing documents
    const missingFormatted = formatCONumbers(missingDocNumbers);
    const missingText = missingDocNumbers.length === 1 ? `Change Order ${missingFormatted}` : `Change Orders ${missingFormatted}`;

    // CASE 6: Final summary list - only print when more than one problematic CO OR when they form a range
    const allProblematicNumbers = missingDocNumbers;
    const shouldShowSummary = allProblematicNumbers.length > 1;
    const summaryText = shouldShowSummary
      ? (() => {
          const lowestProblematic = allProblematicNumbers[0];
          const highestProblematic = allProblematicNumbers[allProblematicNumbers.length - 1];
          const allProblematicText =
            lowestProblematic === highestProblematic ? `#${lowestProblematic}` : `#${lowestProblematic}-${highestProblematic}`;
          return ` The following is a list of all change orders which have not been received or have not been signed by both the Owner and Contractor: ${allProblematicText} for the ${projectDrawName}.`;
        })()
      : '';

    return `New Change Orders ${coNumbersText} (${descriptionText}: ${formattedTotal}) were included with the current application for payment. We have not received a copy of ${missingText}. We leave funding without the fully executed documents to the discretion of the Lender.${summaryText}`;
  } else if (allDocsPresent && insufficientlySigned.length > 0) {
    // Document present but insufficiently signed - ALL docs are present, but some are not fully signed
    // This case does NOT include the summary list
    const signedFormatted = formatCONumbers(insufficientlySignedNumbers);
    const signedText = insufficientlySignedNumbers.length === 1 ? `Change Order ${signedFormatted}` : `Change Orders ${signedFormatted}`;
    const signedVerb = insufficientlySignedNumbers.length === 1 ? 'has' : 'have';

    return `New Change Orders ${coNumbersText} (${descriptionText}: ${formattedTotal}) were included with the current application for payment. ${signedText} ${signedVerb} not been executed by both the Owner and the Contractor. We leave funding without the fully executed document to the discretion of the Lender.`;
  } else {
    // Both missing and insufficiently signed - some docs are missing AND some are insufficiently signed
    const missingFormatted = formatCONumbers(missingDocNumbers);
    const signedFormatted = formatCONumbers(insufficientlySignedNumbers);
    const missingText = missingDocNumbers.length === 1 ? `Change Order ${missingFormatted}` : `Change Orders ${missingFormatted}`;
    const signedText = insufficientlySignedNumbers.length === 1 ? `Change Order ${signedFormatted}` : `Change Orders ${signedFormatted}`;
    const signedVerb = insufficientlySignedNumbers.length === 1 ? 'has' : 'have';

    // CASE 6: Final summary list - only print when more than one problematic CO OR when they form a range
    const allProblematicNumbers = Array.from(new Set([...missingDocNumbers, ...insufficientlySignedNumbers]));
    const shouldShowSummary = allProblematicNumbers.length > 1;
    const summaryText = shouldShowSummary
      ? (() => {
          const lowestProblematic = allProblematicNumbers[0];
          const highestProblematic = allProblematicNumbers[allProblematicNumbers.length - 1];
          const allProblematicText =
            lowestProblematic === highestProblematic ? `#${lowestProblematic}` : `#${lowestProblematic}-${highestProblematic}`;
          return ` The following is a list of all change orders which have not been received or have not been signed by both the Owner and Contractor: ${allProblematicText} for the ${projectDrawName}.`;
        })()
      : '';

    return `New Change Orders ${coNumbersText} (${descriptionText}: ${formattedTotal}) were included with the current application for payment. We have not received a copy of ${missingText}, and ${signedText} ${signedVerb} not been executed by both the Owner and the Contractor. We leave funding without the fully executed documents to the discretion of the Lender.${summaryText}`;
  }
};

const ChangeOrdersSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState, registerSectionSync, unregisterSectionSync } = useMagicReport();
  const projectTitle = reportState.projectSummary?.projectTitle || '';
  const singleChangeOrderThreshold = reportState.projectSummary?.singleChangeOrderThreshold;
  const aggregateChangeOrderThreshold = reportState.projectSummary?.aggregateChangeOrderThreshold;

  // Every covered draw, oldest application first. Reports opened before multi-draw have no list,
  // so the single primary-draw mirror becomes a one-draw list and this renders exactly as before.
  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    if (!reportState.drawSummary) return [];
    return [
      {
        drawId: reportState.drawSummary.id,
        drawSummary: reportState.drawSummary,
        budgetSummary: reportState.budgetSummary,
        lineItems: reportState.lineItems,
      },
    ];
  }, [reportState.draws, reportState.drawSummary, reportState.budgetSummary, reportState.lineItems]);

  /**
   * Draws whose Change Orders block is suppressed for this report.
   *
   * `draws` stays the full covered set everywhere row storage is concerned — hidden draws keep
   * their saved rows so unhiding one restores them — while `visibleDraws` drives everything the
   * reader sees or that is derived across draws: the tables, the summary totals, the single
   * combined comment, and the threshold warnings.
   */
  const hiddenCoLienDrawIds = useMemo(
    () =>
      resolveHiddenCoLienDrawIds(
        reportState.hiddenCoLienDrawIds,
        draws.map(d => d.drawId),
      ),
    [reportState.hiddenCoLienDrawIds, draws],
  );
  const visibleDraws = useMemo(() => filterVisibleCoLienDraws(draws, hiddenCoLienDrawIds), [draws, hiddenCoLienDrawIds]);

  // The primary draw among those still shown: it owns the un-suffixed summary fields the PDF and
  // Action Items fall back to, which must never point at a draw the report doesn't display.
  const primaryDrawId = visibleDraws.length > 0 ? visibleDraws[visibleDraws.length - 1].drawId : undefined;
  // Storage anchor for legacy rows saved without a draw id — always the first COVERED draw, hidden
  // or not, so hiding a draw can never re-home another draw's rows.
  const firstDrawId = draws[0]?.drawId;

  // Name used in the aggregated comment's summary sentence.
  const drawName = reportState.drawSummary?.name || '';
  const projectDrawName = projectTitle && drawName ? `${projectTitle} - ${drawName}` : projectTitle || drawName || '[G702 name]';

  // Stable refs so effects that sync derived data don't re-run on identity changes
  const handleFieldChangeRef = React.useRef(handleFieldChange);
  handleFieldChangeRef.current = handleFieldChange;
  const getFieldRef = React.useRef(getField);
  getFieldRef.current = getField;

  const rowsField = getField(ROWS_FIELD);
  const addToActionItems = rowsField?.addToActionItems || false;

  /** This draw's change-order line items, the pool every row for this draw is matched against. */
  const changeOrderItemsForDraw = useCallback(
    (draw: DrawContext) =>
      (draw.lineItems || []).filter(li => {
        if (li.isDeleted) return false;
        return (li.labels || []).includes(LineItemLabelEnum.ChangeOrder);
      }),
    [],
  );

  /**
   * Seeds a draw that has no saved rows at all, from its own change-order line items.
   *
   * Deliberately ignores other draws' saved rows: rows are keyed on the LineItem now, so matching
   * across draws would copy another draw's reviewer-entered amounts onto a fresh row.
   */
  const buildRowsForDraw = useCallback(
    (draw: DrawContext): ChangeOrderRow[] =>
      changeOrderItemsForDraw(draw).map((li, index) => ({
        lineItemId: changeOrderRowKey(li),
        drawId: draw.drawId,
        coNumber: String(index + 1),
        description: li.lineItem?.description || '',
        doWeHaveDoc: 'No' as const,
        daysExtended: null,
        contractor: 'No' as const,
        architect: 'No' as const,
        owner: 'No' as const,
        // New to the table, so new this period: the amount goes in Current and moves to Previous
        // at the next rollover. See ChangeOrdersTable.buildRowsForNewLineItems.
        prevCO: 0,
        currCO: li.changeOrder || 0,
      })),
    [changeOrderItemsForDraw],
  );

  /**
   * Every draw's rows, in one list. Rows saved before multi-draw carry no `drawId` and are bound
   * to the first covered draw so they keep showing up in its table.
   */
  const allRows: ChangeOrderRow[] = useMemo(() => {
    const parsed =
      rowsField?.entries
        ?.map(entry => {
          try {
            return normalizeRow(JSON.parse(entry.value || '') as ChangeOrderRow);
          } catch {
            return null;
          }
        })
        .filter((r): r is ChangeOrderRow => r !== null) || [];

    const coveredDrawIds = new Set(draws.map(d => d.drawId));
    const coItemsByDraw = new Map(draws.map(d => [d.drawId, changeOrderItemsForDraw(d)]));

    // Rows whose draw this report doesn't cover — carried forward by a reset performed before
    // the server re-anchored them, or left behind by a change to the draw set. They belong to no
    // table and would silently vanish, so bind them to the covered draws positionally, in the
    // order the stale draws first appear (draw lists run oldest application first).
    const staleDrawIds: string[] = [];
    parsed.forEach(r => {
      const id = r.drawId || firstDrawId;
      if (id && !coveredDrawIds.has(id) && !staleDrawIds.includes(id)) staleDrawIds.push(id);
    });
    const anchorDrawId = (drawId?: string): string | undefined => {
      const id = drawId || firstDrawId;
      if (!id || coveredDrawIds.has(id)) return id;
      const index = staleDrawIds.indexOf(id);
      return draws[Math.min(index, draws.length - 1)]?.drawId ?? firstDrawId;
    };

    // Normalizes a saved row onto its budget line's primary key, so rows briefly written with the
    // LineItem id are matched back to the DrawLineItem they belong to. Without this, "Get Change
    // Orders" would neither see the row's amount change nor recognise the row as already covering
    // that budget line, and would append a duplicate.
    const bindRowToLineItem = (row: ChangeOrderRow, drawItems: ProjectLineItemDTO[] | undefined): ChangeOrderRow => {
      const match = (drawItems || []).find(li => rowMatchesLineItem(row, li));
      return match ? { ...row, lineItemId: changeOrderRowKey(match) } : row;
    };

    const savedRows: ChangeOrderRow[] = [];
    const seen = new Set<string>();
    parsed.forEach(row => {
      const drawId = anchorDrawId(row.drawId);
      const migrated = bindRowToLineItem({ ...row, drawId }, coItemsByDraw.get(drawId || ''));
      // Two stale draws collapsing onto one covered draw can produce the same row twice; the
      // table keys its rows on lineItemId, so keep the first and drop the collision.
      const key = `${migrated.drawId || ''}::${migrated.lineItemId}`;
      if (seen.has(key)) return;
      seen.add(key);
      savedRows.push(migrated);
    });

    if (savedRows.length > 0) {
      // Seed any draw that has change-order line items but no saved rows yet.
      const drawsWithoutRows = draws.filter(d => !savedRows.some(r => r.drawId === d.drawId));
      const seeded = drawsWithoutRows.flatMap(d => buildRowsForDraw(d));
      return [...savedRows, ...seeded];
    }

    return draws.flatMap(d => buildRowsForDraw(d));
  }, [rowsField, draws, firstDrawId, buildRowsForDraw, changeOrderItemsForDraw]);

  const rowsByDraw = useMemo(() => {
    const grouped = new Map<string, ChangeOrderRow[]>();
    draws.forEach(d => grouped.set(d.drawId, []));
    allRows.forEach(row => {
      const key = row.drawId || firstDrawId;
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    });
    return grouped;
  }, [allRows, draws, firstDrawId]);

  /**
   * Authoritative row list for merging writes.
   *
   * All draws' tables write to the same `ChangeOrdersRows` field, and several are mounted at once
   * (each auto-saves on mount). Merging against a render-time snapshot would let two writes in the
   * same tick clobber each other, so the merge point below updates this ref synchronously — the
   * second write in a tick sees the first one's result.
   */
  const allRowsRef = React.useRef<ChangeOrderRow[]>(allRows);
  allRowsRef.current = allRows;

  /**
   * Single write point for the shared rows field: serializes every draw's rows and advances the
   * ref synchronously so a second write in the same tick sees this one's result. `updates` carries
   * anything else the caller was setting on the field (a bookmark toggle, say) through untouched.
   */
  const writeAllRows = useCallback((nextRows: ChangeOrderRow[], updates?: Partial<SectionFieldDTO>) => {
    allRowsRef.current = nextRows;
    handleFieldChangeRef.current(ROWS_FIELD, {
      fieldType: FieldType.TextArray,
      ...updates,
      entries: nextRows.map((row, index) => ({ value: JSON.stringify(row), order: index })),
    });
  }, []);

  /**
   * A `handleFieldChange` scoped to one draw's table. Writes to the shared rows field are merged:
   * this draw's rows are replaced, every other draw's are left untouched. Anything else passes
   * straight through. This keeps ChangeOrdersTable unchanged — it still thinks it owns the field.
   */
  const makeDrawScopedFieldChange = useCallback(
    (drawId: string) => (fieldName: string, updates: Partial<SectionFieldDTO>) => {
      if (fieldName !== ROWS_FIELD || !updates.entries) {
        handleFieldChangeRef.current(fieldName, updates);
        return;
      }
      const mine = updates.entries
        .map((entry): ChangeOrderRow | null => {
          try {
            return { ...normalizeRow(JSON.parse(entry.value || '') as ChangeOrderRow), drawId };
          } catch {
            return null;
          }
        })
        .filter((r): r is ChangeOrderRow => r !== null);
      const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
      writeAllRows([...others, ...mine], updates);
    },
    [firstDrawId, writeAllRows],
  );

  /**
   * Persists the budget-derived seed rows.
   *
   * Each table is handed a synthetic rows field holding only its own draw's rows (see
   * `scopedRowsField` below), so ChangeOrdersTable's own "no saved rows -> auto-save" guard never
   * sees an empty field and never fires. Without this the seeded rows would live only in the
   * `allRows` memo: they render, but `ChangeOrdersRows.entries` stays empty on save, so there is
   * nothing for the reset transform to carry forward and mark as last month's.
   *
   * Runs once per mount. The ref is also set when saved rows are already present, so deleting
   * every row can't be mistaken for "never seeded" and immediately re-seed the table.
   */
  const hasSeededRowsRef = React.useRef(false);
  useEffect(() => {
    if (hasSeededRowsRef.current) return;
    if ((rowsField?.entries?.length ?? 0) > 0) {
      hasSeededRowsRef.current = true;
      return;
    }
    if (allRows.length === 0) return;
    hasSeededRowsRef.current = true;
    writeAllRows(allRows);
  }, [rowsField, allRows, writeAllRows]);

  const commentsField = getCommentsField(data, {
    isCommentField: (name, type) => type === FieldType.TextArray && name.toLowerCase().includes('comment'),
  });

  /** Summary values for one draw, from that draw's own line items. */
  const computeSummaryValues = (draw: DrawContext) => {
    const filteredLineItems = (draw.lineItems || []).filter(li => !li.isDeleted);
    const originalContractSum = filteredLineItems.reduce((sum, li) => sum + (li.originalBudget || 0), 0);
    const totalNetChangeOrder = filteredLineItems.reduce((sum, li) => sum + (li.changeOrder || 0), 0);
    const totalNetReallocation = filteredLineItems.reduce((sum, li) => sum + (li.reallocation || 0), 0);
    return {
      originalContractSum,
      totalNetChangeOrder,
      totalNetReallocation,
      contractSumToDate: originalContractSum + totalNetChangeOrder + totalNetReallocation,
    };
  };

  const summaryValuesByDraw = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeSummaryValues>>();
    draws.forEach(d => map.set(d.drawId, computeSummaryValues(d)));
    return map;
  }, [draws]);

  // Persist each draw's summary values under its own field names, and keep the un-suffixed
  // originals pointed at the primary draw so the PDF export and Action Items are unchanged for
  // single-draw reports.
  useEffect(() => {
    summaryValuesByDraw.forEach((values, drawId) => {
      const write = (base: string, value: number) => {
        handleFieldChangeRef.current(changeOrdersSummaryFieldName(base, drawId), { value: value.toString() });
        if (drawId === primaryDrawId) {
          handleFieldChangeRef.current(base, { value: value.toString() });
        }
      };
      write(SUMMARY_ORIGINAL_CONTRACT_SUM, values.originalContractSum);
      write(SUMMARY_TOTAL_NET_CHANGE_ORDER, values.totalNetChangeOrder);
      write(SUMMARY_TOTAL_NET_REALLOCATION, values.totalNetReallocation);
      write(SUMMARY_CONTRACT_SUM_TO_DATE, values.contractSumToDate);
    });
  }, [summaryValuesByDraw, primaryDrawId]);

  /**
   * The aggregated comment, the threshold checks and the totals are driven by every VISIBLE
   * draw's change orders together. A hidden draw's rows stay in `allRows` (and so keep being
   * saved), but must not reach anything the reader sees.
   */
  const rows = useMemo(
    () => allRows.filter(r => !isCoLienHidden(hiddenCoLienDrawIds, r.drawId || firstDrawId)),
    [allRows, hiddenCoLienDrawIds, firstDrawId],
  );
  const lineItems = useMemo(() => visibleDraws.flatMap(d => d.lineItems || []), [visibleDraws]);
  const summaryValues = useMemo(() => {
    let originalContractSum = 0;
    let totalNetChangeOrder = 0;
    let totalNetReallocation = 0;
    let contractSumToDate = 0;
    visibleDraws.forEach(d => {
      const v = summaryValuesByDraw.get(d.drawId);
      if (!v) return;
      originalContractSum += v.originalContractSum;
      totalNetChangeOrder += v.totalNetChangeOrder;
      totalNetReallocation += v.totalNetReallocation;
      contractSumToDate += v.contractSumToDate;
    });
    return { originalContractSum, totalNetChangeOrder, totalNetReallocation, contractSumToDate };
  }, [summaryValuesByDraw, visibleDraws]);

  // Generate comment based on rows data - serialize rows to detect changes
  const rowsKey = useMemo(() => JSON.stringify(rows), [rows]);

  // Track the initial rowsKey to detect when rows actually change
  const initialRowsKeyRef = useRef<string | null>(null);
  if (initialRowsKeyRef.current === null) {
    initialRowsKeyRef.current = rowsKey;
  }

  // Rows have changed if the current rowsKey differs from the initial one
  const rowsHaveChanged = rowsKey !== initialRowsKeyRef.current;

  // Uses refs so identity changes to getField/handleFieldChange don't re-trigger this effect.
  useEffect(() => {
    if (rows.length > 0) {
      const autoComment1 = generateComment1(rows, lineItems || [], projectDrawName);
      const currentComment1 = (getFieldRef.current(COMMENT_1_FIELD)?.value || '').trim();

      // Only update comment if:
      // 1. Comment is empty (no saved comment), OR
      // 2. Rows have changed since last comment update (table modifications)
      const shouldUpdateComment = !currentComment1 || rowsHaveChanged;

      if (shouldUpdateComment && autoComment1) {
        handleFieldChangeRef.current(COMMENT_1_FIELD, { value: autoComment1 });
        // Reset the reference point - now track changes from THIS state forward
        initialRowsKeyRef.current = rowsKey;
      }
    } else {
      const noCOComment = 'No new change orders were included with the current application for payment.';
      const currentComment1 = (getFieldRef.current(COMMENT_1_FIELD)?.value || '').trim();

      // Only update if empty or rows have changed
      const shouldUpdateComment = !currentComment1 || rowsHaveChanged;
      if (shouldUpdateComment) {
        handleFieldChangeRef.current(COMMENT_1_FIELD, { value: noCOComment });
        // Reset the reference point
        initialRowsKeyRef.current = rowsKey;
      }
      handleFieldChangeRef.current(COMMENT_2_FIELD, { value: '' });
    }
  }, [rowsKey, rows.length, lineItems, projectDrawName, rowsHaveChanged]);

  const hasCurrentCORows = useMemo(() => rows.some(r => r.currCO && r.currCO !== 0), [rows]);

  useEffect(() => {
    const currentComment2 = (getFieldRef.current(COMMENT_2_FIELD)?.value || '').trim();
    // Only auto-clear when the field still holds the default text (i.e. not user-modified)
    const isDefaultText = currentComment2 === COMMENT_2_DEFAULT_TEXT;
    if (hasCurrentCORows && !currentComment2) {
      handleFieldChangeRef.current(COMMENT_2_FIELD, { value: COMMENT_2_DEFAULT_TEXT });
    } else if (!hasCurrentCORows && (isDefaultText || !currentComment2)) {
      handleFieldChangeRef.current(COMMENT_2_FIELD, { value: '' });
    }
  }, [rowsKey, hasCurrentCORows]);

  const comment1Field = getField(COMMENT_1_FIELD);
  const comment2Field = getField(COMMENT_2_FIELD);

  const handleRefreshComment2 = useCallback(() => {
    handleFieldChange(COMMENT_2_FIELD, { value: hasCurrentCORows ? COMMENT_2_DEFAULT_TEXT : '' });
  }, [hasCurrentCORows, handleFieldChange]);

  // One sync function per draw's table; "Run this Month" runs them all.
  const syncFnsRef = React.useRef<Map<string, () => Promise<void>>>(new Map());
  React.useEffect(() => {
    const syncId = 'changeOrders';
    registerSectionSync(syncId, async () => {
      await Promise.all(Array.from(syncFnsRef.current.values()).map(fn => fn()));
    });
    return () => {
      unregisterSectionSync(syncId);
    };
  }, [registerSectionSync, unregisterSectionSync]);

  // Totals per draw, so each draw's summary can flag its own mismatch.
  const totalsMatchByDraw = useMemo(() => {
    const map = new Map<string, boolean>();
    draws.forEach(d => {
      const drawRows = rowsByDraw.get(d.drawId) || [];
      const tableTotal = drawRows.reduce((sum, row) => sum + getRowCOValue(row), 0);
      const summaryTotal = summaryValuesByDraw.get(d.drawId)?.totalNetChangeOrder ?? 0;
      map.set(d.drawId, Math.abs(tableTotal - summaryTotal) < 0.01);
    });
    return map;
  }, [draws, rowsByDraw, summaryValuesByDraw]);

  // Calculate total from actual rows across every table
  const actualTableTotal = useMemo(() => {
    return rows.reduce((sum, row) => sum + getRowCOValue(row), 0);
  }, [rows]);

  // Calculate threshold warning message
  const thresholdWarning = useMemo(() => {
    if (!rows.length || (!singleChangeOrderThreshold && !aggregateChangeOrderThreshold)) {
      return null;
    }

    // Check if any single change order exceeds the single threshold
    const hasSingleExceedingThreshold = singleChangeOrderThreshold
      ? rows.some(row => getRowCOValue(row) > singleChangeOrderThreshold)
      : false;

    // Check if aggregate total exceeds the aggregate threshold
    const aggregateExceedsThreshold = aggregateChangeOrderThreshold ? actualTableTotal > aggregateChangeOrderThreshold : false;

    // Rule 1: If any single change order exceeds threshold, show message 1
    if (hasSingleExceedingThreshold) {
      return "Note that one or more of the new change orders is above the lender's approval threshold.";
    }

    // Rule 2: If only aggregate exceeds threshold (and no single exceeds), show message 2
    if (aggregateExceedsThreshold && !hasSingleExceedingThreshold) {
      return "Note that the total of the all change orders is now above the lender's approval threshold.";
    }

    return null;
  }, [rows, singleChangeOrderThreshold, aggregateChangeOrderThreshold, actualTableTotal]);

  const layout = useMemo(
    () => ({
      singleColumn: [
        // One labelled table + its own summary per covered draw. Draws are always labelled — with a
        // single draw this just names the contract, which is harmless and consistent.
        ...visibleDraws.flatMap((draw, drawIndex) => {
          const drawId = draw.drawId;
          const drawRows = rowsByDraw.get(drawId) || [];
          const drawSummaryValues = summaryValuesByDraw.get(drawId) ?? {
            originalContractSum: 0,
            totalNetChangeOrder: 0,
            totalNetReallocation: 0,
            contractSumToDate: 0,
          };
          const drawTotalsMatch = totalsMatchByDraw.get(drawId) ?? true;
          const scopedFieldChange = makeDrawScopedFieldChange(drawId);
          // The table reads its rows from this field, so hand it only this draw's entries.
          const scopedRowsField: SectionFieldDTO | undefined = rowsField
            ? { ...rowsField, entries: drawRows.map((row, index) => ({ value: JSON.stringify(row), order: index })) }
            : undefined;
          const label = draw.drawSummary?.name || `Draw ${drawIndex + 1}`;

          const summaryField = (base: string) =>
            getField(changeOrdersSummaryFieldName(base, drawId)) ?? (drawId === primaryDrawId ? getField(base) : undefined);

          return [
            {
              fieldName: `${ROWS_FIELD}__${drawId}`,
              type: 'custom-component' as const,
              label: '',
              renderComponent: () => (
                <Box sx={{ mt: drawIndex === 0 ? 0 : 3 }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                    {label}
                  </Typography>
                  <ChangeOrdersTable
                    rowsField={scopedRowsField}
                    handleFieldChange={scopedFieldChange}
                    lineItems={draw.lineItems}
                    onRowsSynced={updated => {
                      // Regenerate the single comment from this draw's synced rows plus every
                      // other draw's current rows.
                      const others = allRowsRef.current.filter(r => (r.drawId || firstDrawId) !== drawId);
                      const combined = [...others, ...updated.map(r => ({ ...r, drawId }))];
                      // Hidden draws are still in `others` because they still have to be saved,
                      // so drop them before anything reader-facing is derived.
                      const visible = combined.filter(r => !isCoLienHidden(hiddenCoLienDrawIds, r.drawId || firstDrawId));
                      const autoComment1 = generateComment1(visible, lineItems || [], projectDrawName);
                      handleFieldChange(COMMENT_1_FIELD, { value: autoComment1 });

                      const anyCurrentCO = visible.some(r => r.currCO && r.currCO !== 0);
                      if (anyCurrentCO && !comment2Field?.value) {
                        handleFieldChange(COMMENT_2_FIELD, { value: COMMENT_2_DEFAULT_TEXT });
                      } else if (!anyCurrentCO) {
                        const c2 = (comment2Field?.value || '').trim();
                        if (!c2 || c2 === COMMENT_2_DEFAULT_TEXT) {
                          handleFieldChange(COMMENT_2_FIELD, { value: '' });
                        }
                      }
                    }}
                    onSyncFunctionReady={syncFn => {
                      syncFnsRef.current.set(drawId, syncFn);
                    }}
                    totalsMatch={drawTotalsMatch}
                  />
                </Box>
              ),
            },
            // This draw's summary table
            {
              fieldName: `ChangeOrdersSummary__${drawId}`,
              type: 'custom-component' as const,
              label: '',
              renderComponent: () => (
                <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Box
                    component='table'
                    sx={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      flex: 1,
                      '& tbody tr:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9',
                      },
                      '& tbody tr:nth-of-type(even)': {
                        backgroundColor: '#ffffff',
                      },
                      '& td': {
                        padding: '8px 12px',
                        borderBottom: '1px solid #e0e0e0',
                        fontSize: '0.875rem',
                        verticalAlign: 'middle',
                      },
                      '& td:first-of-type': {
                        fontWeight: 'medium',
                      },
                      '& td:last-of-type': {
                        textAlign: 'right',
                      },
                    }}
                  >
                    <tbody>
                      <tr>
                        <td>Original Contract Sum:</td>
                        <td>
                          {summaryField(SUMMARY_ORIGINAL_CONTRACT_SUM)?.value
                            ? formatCurrency(summaryField(SUMMARY_ORIGINAL_CONTRACT_SUM)!.value as string, true)
                            : formatCurrency(drawSummaryValues.originalContractSum.toString(), true)}
                        </td>
                      </tr>
                      <tr>
                        <td>Total Net Change Order Amount:</td>
                        <td
                          style={{
                            backgroundColor: !drawTotalsMatch ? '#ffebee' : undefined,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {!drawTotalsMatch && (
                              <Tooltip title='Change order amount mismatch' arrow>
                                <WarningIcon sx={{ fontSize: 18, color: 'error.main' }} />
                              </Tooltip>
                            )}
                            <span>
                              {summaryField(SUMMARY_TOTAL_NET_CHANGE_ORDER)?.value
                                ? formatCurrency(summaryField(SUMMARY_TOTAL_NET_CHANGE_ORDER)!.value as string, true)
                                : formatCurrency(drawSummaryValues.totalNetChangeOrder.toString(), true)}
                            </span>
                          </Box>
                        </td>
                      </tr>
                      <tr>
                        <td>Total Net Reallocation Amount:</td>
                        <td>
                          {summaryField(SUMMARY_TOTAL_NET_REALLOCATION)?.value
                            ? formatCurrency(summaryField(SUMMARY_TOTAL_NET_REALLOCATION)!.value as string, true)
                            : formatCurrency(drawSummaryValues.totalNetReallocation.toString(), true)}
                        </td>
                      </tr>
                      <tr>
                        <td>Contract Sum To Date:</td>
                        <td>
                          {summaryField(SUMMARY_CONTRACT_SUM_TO_DATE)?.value
                            ? formatCurrency(summaryField(SUMMARY_CONTRACT_SUM_TO_DATE)!.value as string, true)
                            : formatCurrency(drawSummaryValues.contractSumToDate.toString(), true)}
                        </td>
                      </tr>
                    </tbody>
                  </Box>
                  {/* Bookmarking is per section, not per draw — shown once, on the first draw. */}
                  {drawIndex === 0 ? (
                    <IconButton
                      size='small'
                      onClick={() => handleFieldChange(ROWS_FIELD, { addToActionItems: !addToActionItems })}
                      sx={{ mt: 3, flexShrink: 0 }}
                    >
                      {addToActionItems ? (
                        <BookmarkIcon color='primary' sx={{ fontSize: 20 }} />
                      ) : (
                        <BookmarkBorderIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      )}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 30, flexShrink: 0 }} />
                  )}
                </Box>
              ),
            },
          ];
        }),
        // Always show comment1 if there are rows or if it has a value
        ...(rows.length > 0 || comment1Field?.value
          ? [
              {
                fieldName: COMMENT_1_FIELD,
                type: 'text' as const,
                label: '',
              },
            ]
          : []),
        // Always show comment2 if there are rows or if it has a value
        ...(rows.length > 0 || comment2Field?.value
          ? [
              {
                fieldName: COMMENT_2_FIELD,
                type: 'text-with-refresh' as const,
                label: '',
                onRefresh: handleRefreshComment2,
              },
            ]
          : []),
      ],
    }),
    [
      visibleDraws,
      hiddenCoLienDrawIds,
      rowsField,
      rowsByDraw,
      summaryValuesByDraw,
      totalsMatchByDraw,
      makeDrawScopedFieldChange,
      primaryDrawId,
      firstDrawId,
      getField,
      handleFieldChange,
      lineItems,
      projectDrawName,
      addToActionItems,
      rows.length,
      hasCurrentCORows,
      comment1Field,
      comment2Field,
      handleRefreshComment2,
    ],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {thresholdWarning && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          {thresholdWarning}
        </Alert>
      )}
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange(commentsField.fieldName, updated)}
          onBook={onBook}
        />
      )}
    </Box>
  );
};

export const ChangeOrdersSection: React.FC<{ magicReportId: string }> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={CHANGE_ORDERS_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ChangeOrdersSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
